import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  GridComponent,
  Inject,
  Search,
  Page,
  Sort,
  Filter,
  ExcelExport,
  Toolbar,
} from "@syncfusion/ej2-react-grids";
import * as FileSaver from "file-saver";
import * as XLSX from "xlsx";
import { connect } from "react-redux";
import { Header } from "../../../components";
import { useNavigate } from "react-router-dom";
import axios from "../../../api";
import {
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
} from "@material-ui/core";
import { encrypt } from "../../../utils/crypt";
import Swal from "sweetalert2";
import BackdropComponent from "../../../components/Backdrop";

const searchOptionsField = {
  fields: ["nombre"],
  ignoreCase: true,
  hierarchyMode: "None",
  operator: "contains",
  key: "",
};

const toolbarOptsField = [
  "Search",
  { text: "Crear grupo", prefixIcon: "e-add", id: "add_new_group", align: "Left" },
  { text: "Exportar", prefixIcon: "e-excelexport", id: "export_excel", align: "Left" },
];

const GradeGroups = (props) => {
  const history = useNavigate();
  const { token, rawPermission, userId } = props;
  const isMounted = useRef(true);

  const [gradeGroups, setGradeGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const permission = useMemo(() => (
    (props.rawPermission || [])
      .filter(d => d?.modulosAcciones?.id_modulos === 15)
      .map(i => i?.modulosAcciones?.id_acciones)
  ), [props.rawPermission]);
  
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 12;

  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  // Función para obtener periodos (mejorada con fallback robusto)
  const getPeriods = async () => {
    try {
      const res = await axios.post("/period/getPeriods", {}, {
        headers: { "access-token": token },
      });
      if (isMounted.current && res?.data?.success) {
        const list = res.data.periods || res.data.data || [];
        setPeriods(list);
        if (list.length > 0 && !selectedPeriod) {
          setSelectedPeriod(list[0].id);
        }
      }
    } catch (error) {
      console.error("Error fetching periods:", error);
      if (isMounted.current) setPeriods([]);
    }
  };

  // Función para obtener grupos
  const getGradeGroups = async () => {
    if (!selectedPeriod) return;
    setLoading(true);
    try {
      const payload = { id_periodos: selectedPeriod };
      
      // Si el usuario escribió algo en el buscador, lo enviamos al backend
      if (searchTerm) {
        payload.nombre = searchTerm;
      }

      const res = await axios.post(
        `/gradeGroup/getGradeGroups?page=${currentPage}&limit=${pageSize}`,
        payload,
        { headers: { "access-token": token } }
      );
      if (isMounted.current && res?.data) {
        setGradeGroups(res.data.gradeGroups || []);
        setTotalRecords(res.data.total || 0);
      }
    } catch (error) {
      console.error("Error fetching groups:", error);
      if (isMounted.current) setGradeGroups([]);
    } finally {
      if (isMounted.current) setLoading(false);
    }
  };

  useEffect(() => {
    if (token && Array.isArray(permission) && permission.includes(1) && periods.length === 0) {
      getPeriods();
    }
  }, [token, permission, periods.length]);

  useEffect(() => {
    if (selectedPeriod && token) {
      getGradeGroups();
    }
  }, [selectedPeriod, currentPage, searchTerm, token]);

  const handleToolbarClick = (args) => {
    if (args.item.id === "add_new_group") {
      history(`/gradeGroups/create`);
    } else if (args.item.id === "export_excel") {
      if (!gradeGroups.length) return Swal.fire("Aviso", "No hay datos", "info");
      const ws = XLSX.utils.json_to_sheet(gradeGroups.map(i => ({
        Nombre: i.nombre,
        Materia: i.materias?.nombre || "N/A",
        Docente: `${i.docente?.nombres || ""} ${i.docente?.apellidos || ""}`.trim()
      })));
      const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
      const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      FileSaver.saveAs(new Blob([buf], { type: "application/octet-stream" }), "Grupos.xlsx");
    }
  };

  // DEFINICIÓN DE COLUMNAS COMO ARRAY (Más estable para Syncfusion React)
  const columnsGrid = useMemo(() => [
    { field: "nombre", headerText: "Nombre", textAlign: "Center", width: "160" },
    { 
      headerText: "¿Es virtual?", 
      textAlign: "Center", 
      width: "120", 
      template: (row) => (row?.es_virtual ? "Sí" : "No") 
    },
    { 
      headerText: "Docente", 
      width: "250", 
      template: (row) => row?.docente ? `${row.docente.nombres || ""} ${row.docente.apellidos || ""}`.trim() : "N/A" 
    },
    { 
      headerText: "Materia", 
      width: "250", 
      template: (row) => row?.materias?.nombre || "N/A" 
    },
    { 
      headerText: "Editar", 
      width: "110", 
      textAlign: "Center", 
      template: (row) => (
        <Button
          onClick={() => history(`/gradeGroups/edit/${encrypt(row.id)}`)}
          variant="contained"
          size="small"
          style={{ backgroundColor: "#9d2d39", color: "white", borderRadius: "8px" }}
          disabled={!(permission || []).includes(3)}
        >
          Editar
        </Button>
      )
    },
    { 
      headerText: "Eliminar", 
      width: "110", 
      textAlign: "Center", 
      template: (row) => (
        <Button
          onClick={() => {
            Swal.fire({
              text: "¿Desea eliminar este grupo?",
              icon: "warning",
              showCancelButton: true,
              confirmButtonColor: "#d33",
              confirmButtonText: "Aceptar",
            }).then(async (result) => {
              if (result.value) {
                setLoading(true);
                try {
                  const res = await axios.delete(`/gradeGroup/${row.id}`, {
                    data: { id_usuarios: userId },
                    headers: { "access-token": token },
                  });
                  if (res?.data?.gradeGroupDeleted?.success) {
                    Swal.fire({ text: "Eliminado exitosamente.", icon: "success", timer: 2000, showConfirmButton: false });
                    getGradeGroups();
                  }
                } catch (e) { console.error(e); }
                finally { if (isMounted.current) setLoading(false); }
              }
            });
          }}
          variant="contained"
          size="small"
          disabled={!(permission || []).includes(4)}
          style={{ backgroundColor: "#9d2d39", color: "white", borderRadius: "8px" }}
        >
          Eliminar
        </Button>
      )
    }
  ], [permission, history, token, userId]);

  if (!Array.isArray(permission) || permission.length === 0) {
    return <div className="p-20 text-center"><BackdropComponent loading={true} />Cargando permisos...</div>;
  }

  return (
    <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4} flexWrap="wrap">
        <Header category="Parametrización" title="Grupos de Materias" />
        <Box minWidth={250}>
          <FormControl variant="outlined" fullWidth size="small">
            <InputLabel>Filtrar por Periodo</InputLabel>
            <Select
              value={selectedPeriod}
              onChange={(e) => { setSelectedPeriod(e.target.value); setCurrentPage(1); }}
              label="Filtrar por Periodo"
            >
              {periods.map((p) => (
                <MenuItem key={p.id} value={p.id}>{p.nombre}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {selectedPeriod ? (
        <GridComponent
          dataSource={{ result: gradeGroups, count: totalRecords }}
          width="auto"
          allowPaging
          pageSettings={{ pageSize, currentPage }}
          searchSettings={searchOptionsField}
          toolbar={toolbarOptsField}
          toolbarClick={handleToolbarClick}
          actionBegin={(args) => { 
            if (args.requestType === "paging") setCurrentPage(args.currentPage); 
            if (args.requestType === "searching") {
              setSearchTerm(args.searchString);
              setCurrentPage(1); // Volver a la página 1 al buscar
            }
          }}
          columns={columnsGrid}
        >
          <Inject services={[Search, Page, Sort, Filter, Toolbar, ExcelExport]} />
        </GridComponent>
      ) : (
        <Box p={10} textAlign="center"><Typography>Cargue o seleccione un periodo...</Typography></Box>
      )}

      <BackdropComponent loading={loading} />
    </div>
  );
};

const mapStateToProps = (state) => ({
  token: state.token,
  rawPermission: state.permission || [],
  userId: state.user?.id,
});

export default connect(mapStateToProps, null)(GradeGroups);
